import React from 'react';
import { motion } from 'framer-motion';
interface DigitalClockProps {
  hours: number;
  minutes: number;
  seconds?: number;
  showSeconds?: boolean;
  format?: '12h' | '24h';
  label?: string;
  mode?: 'stimulus' | 'support';
  size?: number;
  className?: string;
}
export function DigitalClock({
  hours,
  minutes,
  seconds = 0,
  showSeconds = false,
  format = '24h',
  label,
  mode = 'support',
  size,
  className = ''
}: DigitalClockProps) {
  // Format the time based on 12h/24h setting
  let displayHours = hours;
  let ampm = '';
  if (format === '12h') {
    ampm = hours >= 12 ? 'PM' : 'AM';
    displayHours = hours % 12;
    if (displayHours === 0) displayHours = 12;
  }
  const hStr = displayHours.toString().padStart(2, '0');
  const mStr = minutes.toString().padStart(2, '0');
  const sStr = seconds.toString().padStart(2, '0');
  // Determine sizing based on mode if not explicitly provided
  const isCompact = mode === 'stimulus';
  const paddingClass = isCompact ? 'p-4' : 'p-6 md:p-8';
  const textClass = isCompact ? 'text-4xl md:text-5xl' : 'text-5xl md:text-7xl';
  const ampmClass = isCompact ? 'text-sm' : 'text-xl';
  const getSpokenTime = (h: number, m: number) => {
    const numWords = ['twaalf', 'één', 'twee', 'drie', 'vier', 'vijf', 'zes', 'zeven', 'acht', 'negen', 'tien', 'elf', 'twaalf', 'dertien', 'veertien'];
    const getHourWord = (hour: number) => {
      let h12 = hour % 12;
      if (h12 === 0) h12 = 12;
      return numWords[h12];
    };
    const currentHour = getHourWord(h);
    const nextHour = getHourWord(h + 1);
    if (m === 0) return `${currentHour} uur`;
    if (m === 15) return `kwart over ${currentHour}`;
    if (m === 30) return `half ${nextHour}`;
    if (m === 45) return `kwart voor ${nextHour}`;
    const mWord = (mins: number) => mins <= 14 ? numWords[mins] : mins.toString();
    if (m < 15) return `${mWord(m)} over ${currentHour}`;
    if (m < 30) return `${mWord(30 - m)} voor half ${nextHour}`;
    if (m < 45) return `${mWord(m - 30)} over half ${nextHour}`;
    return `${mWord(60 - m)} voor ${nextHour}`;
  };
  return <motion.div initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className={`flex flex-col items-center gap-3 ${className}`} data-id="element-11726">
      <div className={`relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl md:rounded-3xl border-4 border-slate-700 shadow-2xl overflow-hidden ${paddingClass}`} style={size ? {
      transform: `scale(${size / 100})`,
      transformOrigin: 'center'
    } : undefined} data-id="element-11727">
        {/* Subtle reflection/glare effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" data-id="element-11728" />

        {/* LED Display */}
        <div className="flex items-baseline gap-2 md:gap-3 font-mono font-black text-emerald-400 tracking-wider drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" data-id="element-11729">
          <div className={textClass} data-id="element-11730">{hStr}</div>
          <div className={`${textClass} pb-1 md:pb-2 animate-pulse`} data-id="element-11731">:</div>
          <div className={textClass} data-id="element-11732">{mStr}</div>

          {showSeconds && <>
              <div className={`${textClass} pb-1 md:pb-2 text-emerald-500/80`} data-id="element-11733">
                :
              </div>
              <div className={`${textClass} text-emerald-500/80`} data-id="element-11734">{sStr}</div>
            </>}

          {format === '12h' && <div className={`${ampmClass} ml-2 font-bold text-emerald-500/80 tracking-normal`} data-id="element-11735">
              {ampm}
            </div>}
        </div>
      </div>

      {!isCompact && <div className="bg-slate-100 px-5 py-2.5 rounded-xl border border-slate-200 shadow-sm mt-2" data-id="element-11736">
          <p className="text-slate-700 font-medium text-lg text-center capitalize" data-id="element-11737">
            {getSpokenTime(hours, minutes)}
          </p>
        </div>}

      {label && <p className={`font-semibold text-slate-600 text-center ${isCompact ? 'text-xs' : 'text-sm'}`} data-id="element-11738">
          {label}
        </p>}
    </motion.div>;
}